// src/views/admin/users/customers/dashboard/pages/CustomerOrdersPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector }           from 'react-redux';
import { useNavigate }                        from 'react-router-dom';
import { getCustomerOrdersService } from '@/services/CustomerServices';
import validationErrors                    from '@/lib/validationErrors';
import ToastMe                             from '@/components/ui/ToastMe';
import { setCustomerBearerToken }          from '@/security/Axios';

import RecentOrdersList from '../component/RecentOrdersList';

const CustomerOrdersPage = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const customer  = useSelector((s) => s.Auth?.userData);

  const [orders, setOrders]     = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [q, setQ]               = useState(''); // local search

  // Ensure we’re using the customer JWT for this page
  useEffect(() => {
    const tok = localStorage.getItem('customer_jwt') || localStorage.getItem('customerToken');
    if (!tok) {
      ToastMe('Please log in to view your orders.', 'error');
      navigate('/customer/login');
      return;
    }
    setCustomerBearerToken(tok);
  }, [navigate]);

  useEffect(() => {
    if (!customer?._id) return;

    setLoading(true);
    dispatch(getCustomerOrdersService({ customerId: customer._id, limit: 50 }))
      .then((res) => setOrders(Array.isArray(res?.data?.data) ? res.data.data : []))
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setLoading(false));
  }, [customer, dispatch]);

  // Client-side search by order no / item names
  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((o) => {
      const orderNo = String(o?.order_no || o?.orderNo || o?._id || '').toLowerCase();
      const items   =
        (Array.isArray(o?.items) ? o.items : Array.isArray(o?.cartItems) ? o.cartItems : [])
          .map((i) => String(i?.name || '').toLowerCase())
          .join(' ');
      return orderNo.includes(term) || items.includes(term);
    });
  }, [orders, q]);

  return (
    <section className="pt-8 pb-16 min-h-[680px]">
      <div className="container max-w-[1000px]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-heading">My Orders</h2>
            <p className="text-sm text-gray-500">Track your recent purchases and reorder fast.</p>
          </div>

          {/* Search */}
          <div className="w-full sm:w-[320px]">
            <div className="relative">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by order # or item…"
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              />
              <i className="lab lab-search absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        </div>

        {/* List */}
        <RecentOrdersList
          orders={visible}
          isLoading={isLoading}
          onView={(orderId) => navigate(`/customer/order/${orderId}`)}
        />
      </div>
    </section>
  );
};

export default CustomerOrdersPage;
